import React from "react";
import clsx from "clsx";

import { t } from "../../i18n";

import "./AnimationTimeline.scss";

import type { AppClassProperties } from "../../types";
import type { UIAppState } from "../../types";

const AnimationTimeline = ({
  app,
  appState,
  isMobile = false,
}: {
  app: AppClassProperties;
  appState: UIAppState;
  isMobile?: boolean;
}) => {
  const { animation } = appState;
  const { frameIndex, frames, isPlaying, fps, onionSkinning } = animation;
  const canDelete = frames.length > 1;
  const onionOn = onionSkinning.prev > 0 || onionSkinning.next > 0;
  const fpsPresets = [6, 12, 24, 30];
  const fpsOptions = fpsPresets.includes(fps)
    ? fpsPresets
    : [...fpsPresets, fps].sort((a, b) => a - b);

  return (
    <div
      className={clsx("animation-timeline", {
        "animation-timeline--mobile": isMobile,
      })}
      role="region"
      aria-label={t("labels.animationTimeline")}
    >
      <div className="animation-timeline__strip" role="list">
        {frames.map((_, i) => (
          <button
            key={i}
            type="button"
            className={clsx("animation-timeline__frame", {
              "animation-timeline__frame--current": i === frameIndex,
            })}
            onClick={() => app.switchToFrame(i)}
            title={t("labels.frame", { number: i + 1 })}
            aria-label={t("labels.frame", { number: i + 1 })}
            aria-current={i === frameIndex ? "true" : undefined}
            role="listitem"
          >
            <span className="animation-timeline__frame-number">{i + 1}</span>
          </button>
        ))}
      </div>
      <div className="animation-timeline__playback-row">
        <div className="animation-timeline__actions">
          <button
            type="button"
            className="animation-timeline__btn"
            onClick={() => app.togglePlayback()}
            title={isPlaying ? t("labels.pause") : t("labels.play")}
            aria-label={isPlaying ? t("labels.pause") : t("labels.play")}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
          <button
            type="button"
            className={clsx("animation-timeline__btn", {
              "animation-timeline__btn--active": onionOn,
            })}
            onClick={() =>
              app.setOnionSkinning(
                onionOn ? { prev: 0, next: 0 } : { prev: 1, next: 0 },
              )
            }
            title={onionOn ? t("labels.onionViewOn") : t("labels.onionViewOff")}
            aria-label={t("labels.onionView")}
            aria-pressed={onionOn}
          >
            🧅
          </button>
          <div
            className="animation-timeline__fps"
            title={t("labels.playbackSpeed")}
          >
            <select
              id="animation-fps"
              className="animation-timeline__fps-select"
              value={fps}
              onChange={(e) => app.setAnimationFps(Number(e.target.value))}
              aria-label={t("labels.playbackSpeed")}
            >
              {fpsOptions.map((n) => (
                <option key={n} value={n}>
                  {t("labels.fps", { fps: n })}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="animation-timeline__btn"
            onClick={() => app.addFrame()}
            title={t("labels.addFrame")}
            aria-label={t("labels.addFrame")}
          >
            +
          </button>
          <button
            type="button"
            className="animation-timeline__btn"
            onClick={() => app.duplicateFrame()}
            title={t("labels.duplicateFrame")}
            aria-label={t("labels.duplicateFrame")}
          >
            ⧉
          </button>
          <button
            type="button"
            className="animation-timeline__btn"
            onClick={() => app.deleteFrame()}
            disabled={!canDelete}
            title={t("labels.deleteFrame")}
            aria-label={t("labels.deleteFrame")}
          >
            −
          </button>
        </div>
        <span className="animation-timeline__label">
          {t("labels.frameOf", {
            current: frameIndex + 1,
            total: frames.length,
          })}
        </span>
      </div>
    </div>
  );
};

export default AnimationTimeline;
